import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../components/common/ToastProvider";
import { useViewport } from "../utils/device";
import { useCareFlowState } from "../hooks/careflow/useCareFlowState";
import { FastBillingWindow } from "../components/careflow/types";
import { blankPatientForm, toPatientProfile } from "../components/careflow/utils";
import PatientSearch from "../components/doctor/PatientSearch";
import BillingPanel from "../components/careflow/BillingPanel";
import PharmacyPanel from "../components/careflow/PharmacyPanel";
import FinancePanel from "../components/careflow/FinancePanel";
import DiagnosisPanel from "../components/careflow/DiagnosisPanel";
import NotesPanel from "../components/careflow/NotesPanel";
import TreatmentPanel from "../components/careflow/TreatmentPanel";
import TodayQueueConsole from "../components/Reception/TodayQueueConsole";
import CareFlow360Workspace from "../components/careflow360/CareFlow360Workspace";
import PatientEditModal from "../components/careflow/PatientEditModal";
import { useI18n } from "../i18n/I18nProvider";
import { apiGet } from "../services/api";

import "./FastBilling.css";
import "./FastBillingMobile.css";

type HubTab = {
  key: FastBillingWindow;
  label: string;
  shortLabel: string;
  icon: string;
  requiresPatient?: boolean;
};

function resolveDefaultWindow(pathname: string, isTouch: boolean): FastBillingWindow {
  const path = String(pathname || "").toLowerCase();
  if (path.startsWith("/pharmacy")) return "pharmacy";
  if (path.startsWith("/manager")) return isTouch ? "billing" : "finance";
  if (path.startsWith("/reception")) return isTouch ? "queue" : "apps";
  if (path.startsWith("/doctor")) return isTouch ? "queue" : "careflow";
  return isTouch ? "queue" : "careflow";
}

export default function FastBilling() {
  const { showToast } = useToast();
  const { t } = useI18n();
  const navigate = useNavigate();
  const viewport = useViewport();
  const isTouchViewport = viewport === "mobile" || viewport === "tablet";

  const state = useCareFlowState();
  const [activeWindow, setActiveWindow] = useState<FastBillingWindow>(() =>
    resolveDefaultWindow(
      typeof window !== "undefined" ? window.location.pathname : "/careflow",
      viewport === "mobile" || viewport === "tablet"
    )
  );
  const [showEditModal, setShowEditModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<{ total: number; paid: number; balance: number; status: string } | null>(null);

  const patientIdValue = state.patient?.patient_id != null ? String(state.patient.patient_id) : "";
  const visitIdValue = state.visit?.visit_id != null ? Number(state.visit.visit_id) : null;
  const hasPatientContext = Boolean(state.patient && visitIdValue);

  const paymentBadge = useMemo(() => {
    if (!paymentStatus) return null;
    const status = paymentStatus.status;
    if (status === "OVER_PAID") return { label: "Overpaid", tone: "over", amount: paymentStatus.balance };
    if (status === "FULL_PAID") return { label: "Paid in Full", tone: "paid", amount: paymentStatus.balance };
    if (status === "UNDER_PAID" || status === "UNPAID") return { label: "Balance Due", tone: "due", amount: paymentStatus.balance };
    return { label: "No Bills", tone: "neutral", amount: 0 };
  }, [paymentStatus]);

  useEffect(() => {
    if (!patientIdValue) {
      setPaymentStatus(null);
      return;
    }
    apiGet(`/api/doctor/patient-payment-status/${encodeURIComponent(patientIdValue)}`)
      .then((data) => {
        const d = data as { total?: number; paid?: number; balance?: number; status?: string };
        setPaymentStatus({
          total: Number(d.total || 0),
          paid: Number(d.paid || 0),
          balance: Number(d.balance || 0),
          status: String(d.status || "NO_BILLS"),
        });
      })
      .catch(() => setPaymentStatus(null));
  }, [patientIdValue]);

  useEffect(() => {
    document.title = "CareFlow Mobile - Thaila Dental";
  }, []);

  const tabs = useMemo<HubTab[]>(() => {
    if (isTouchViewport) {
      return [
        { key: "queue", label: "Today Queue", shortLabel: "Queue", icon: "Q" },
        { key: "diagnosis", label: "Diagnosis", shortLabel: "Dx", icon: "DX", requiresPatient: true },
        { key: "notes", label: "Notes", shortLabel: "Notes", icon: "NT", requiresPatient: true },
        { key: "treatment", label: "Treatment", shortLabel: "Tx", icon: "TX", requiresPatient: true },
        { key: "billing", label: t("mobile_tab_billing"), shortLabel: t("mobile_tab_billing_short"), icon: "BL", requiresPatient: true },
        { key: "pharmacy", label: t("mobile_tab_pharmacy"), shortLabel: t("mobile_tab_pharmacy_short"), icon: "RX" },
      ];
    }

    return [
      {
        key: "careflow",
        label: t("mobile_tab_careflow"),
        shortLabel: t("mobile_tab_careflow_short"),
        icon: "CF",
        requiresPatient: true,
      },
      {
        key: "diagnosis",
        label: "Diagnosis",
        shortLabel: "Dx",
        icon: "DX",
        requiresPatient: true,
      },
      {
        key: "notes",
        label: "Notes",
        shortLabel: "Notes",
        icon: "NT",
        requiresPatient: true,
      },
      {
        key: "treatment",
        label: "Treatment",
        shortLabel: "Tx",
        icon: "TX",
        requiresPatient: true,
      },
      {
        key: "billing",
        label: t("mobile_tab_billing"),
        shortLabel: t("mobile_tab_billing_short"),
        icon: "BL",
        requiresPatient: true,
      },
      {
        key: "pharmacy",
        label: t("mobile_tab_pharmacy"),
        shortLabel: t("mobile_tab_pharmacy_short"),
        icon: "RX",
      },
      {
        key: "finance",
        label: t("mobile_tab_finance"),
        shortLabel: t("mobile_tab_finance_short"),
        icon: "FN",
      },
      {
        key: "apps",
        label: t("mobile_tab_apps"),
        shortLabel: t("mobile_tab_apps_short"),
        icon: "AP",
      },
    ];
  }, [isTouchViewport, t]);

  useEffect(() => {
    const selectedTab = tabs.find((item) => item.key === activeWindow);
    if (!selectedTab) {
      setActiveWindow(tabs[0]?.key || "careflow");
      return;
    }

    if (selectedTab.requiresPatient && !hasPatientContext) {
      setActiveWindow(tabs[0]?.key || "careflow");
    }
  }, [activeWindow, hasPatientContext, tabs]);

  const visibleTab = tabs.find((item) => item.key === activeWindow) ?? tabs[0];

  const appModules = [
    {
      id: "reception",
      label: t("mobile_app_reception"),
      description: t("mobile_app_reception_sub"),
      path: "/reception",
      accent: "#0f766e",
    },
    {
      id: "doctor",
      label: t("mobile_app_doctor"),
      description: t("mobile_app_doctor_sub"),
      path: "/doctor",
      accent: "#0369a1",
    },
    {
      id: "manager",
      label: t("mobile_app_manager"),
      description: t("mobile_app_manager_sub"),
      path: "/manager",
      accent: "#b45309",
    },
    {
      id: "treatments",
      label: t("mobile_app_treatments"),
      description: t("mobile_app_treatments_sub"),
      path: "/treatments",
      accent: "#166534",
    },
    {
      id: "pharmacy",
      label: t("mobile_app_pharmacy"),
      description: t("mobile_app_pharmacy_sub"),
      path: "/pharmacy",
      accent: "#b91c1c",
    },
  ];

  const renderEmpty = (title: string, subtitle: string) => (
    <section className="cfm-empty-state" role="status">
      <h3>{title}</h3>
      <p>{subtitle}</p>
    </section>
  );

  const renderWindow = () => {
    if (activeWindow === "queue") {
      return (
        <section className="cfm-pane">
          <TodayQueueConsole
            onSelectPatient={(patient) => {
              void state.bindPatientContext({
                patient_id: String(patient.patient_id),
                name: patient.name || "",
              });
              setActiveWindow("diagnosis");
            }}
            onEdit={(patient) => {
              const parsedAge = patient.age !== undefined && patient.age !== null ? Number(patient.age) : undefined;
              void state.bindPatientContext({
                patient_id: String(patient.patient_id),
                name: patient.name || "",
                gender: patient.gender,
                age: Number.isFinite(parsedAge) ? parsedAge : undefined,
              });
              setShowEditModal(true);
            }}
          />
        </section>
      );
    }

    if (activeWindow === "careflow") {
      if (!hasPatientContext) {
        return renderEmpty(t("mobile_select_patient_title"), t("mobile_select_patient_sub"));
      }
      return (
        <section className="cfm-pane cfm-pane-careflow">
          <CareFlow360Workspace
            visitId={visitIdValue}
            patientId={patientIdValue}
            patientName={state.patient?.name}
            onClosePanel={state.resetContext}
            onOpenBilling={() => setActiveWindow("billing")}
            onOpenDiagnosis={() => setActiveWindow("diagnosis")}
            onOpenTreatment={() => setActiveWindow("treatment")}
          />
        </section>
      );
    }

    if (activeWindow === "notes") {
      if (!hasPatientContext) {
        return renderEmpty(t("mobile_select_patient_title"), t("mobile_select_patient_sub"));
      }
      return (
        <section className="cfm-pane">
          <NotesPanel
            visitIdValue={visitIdValue}
            patientIdValue={patientIdValue}
            showToast={showToast}
          />
        </section>
      );
    }

    if (activeWindow === "diagnosis") {
      if (!hasPatientContext) {
        return renderEmpty(t("mobile_select_patient_title"), t("mobile_select_patient_sub"));
      }
      return (
        <section className="cfm-pane">
          <DiagnosisPanel
            visitIdValue={visitIdValue}
            patientIdValue={patientIdValue}
            diagnosisOptions={state.diagnosisOptions}
            setDiagnosisOptions={state.setDiagnosisOptions}
            diagnosisMasterOptions={state.diagnosisMaster}
            commonDiagnoses={state.commonDiagnoses}
            visitDiagnoses={state.visitDiagnoses}
            setVisitDiagnoses={state.setVisitDiagnoses}
            showToast={showToast}
          />
        </section>
      );
    }

    if (activeWindow === "treatment") {
      if (!hasPatientContext) {
        return renderEmpty(t("mobile_select_patient_title"), t("mobile_select_patient_sub"));
      }
      return (
        <section className="cfm-pane">
          <TreatmentPanel
            visitIdValue={visitIdValue}
            patientIdValue={patientIdValue}
            catalog={state.catalog}
            catalogLoading={state.catalogLoading}
            consultants={state.consultants}
            consultantServices={state.consultantServices}
            consultantServiceFees={state.consultantServiceFees}
            sessions={state.sessions}
            setSessions={state.setSessions}
            showToast={showToast}
            isTouchViewport={isTouchViewport}
          />
        </section>
      );
    }

    if (activeWindow === "billing") {
      if (!hasPatientContext) {
        return renderEmpty(t("mobile_billing_empty_title"), t("mobile_billing_empty_sub"));
      }
      return (
        <section className="cfm-pane">
          <BillingPanel patient={state.patient} visit={state.visit} />
        </section>
      );
    }

    if (activeWindow === "pharmacy") {
      return (
        <section className="cfm-pane">
          <PharmacyPanel patientIdValue={patientIdValue} showToast={showToast} />
        </section>
      );
    }

    if (activeWindow === "finance") {
      return (
        <section className="cfm-pane">
          <FinancePanel
            consultantServices={state.consultantServices}
            consultantServiceFees={state.consultantServiceFees}
            showToast={showToast}
          />
        </section>
      );
    }

    return (
      <section className="cfm-pane">
        <div className="cfm-app-grid">
          {appModules.map((module) => (
            <button
              key={module.id}
              className="cfm-app-card"
              onClick={() => navigate(module.path)}
              style={{ ["--cfm-accent" as string]: module.accent }}
            >
              <strong>{module.label}</strong>
              <small>{module.description}</small>
            </button>
          ))}
        </div>
      </section>
    );
  };

  return (
    <main className={`cfm-page ${isTouchViewport ? "is-touch" : "is-desktop"}`}>
      <div className="cfm-orb cfm-orb-a" aria-hidden="true" />
      <div className="cfm-orb cfm-orb-b" aria-hidden="true" />
      <section className="cfm-shell">
        <header className="cfm-header">
          <div className="cfm-brand-row">
            <div>
              <p className="cfm-eyebrow">{t("mobile_brand_eyebrow")}</p>
              <h1>{t("mobile_brand_title")}</h1>
            </div>
            <div className="cfm-brand-badge">{visibleTab.label}</div>
          </div>

          <div className="cfm-search-row">
            <div className="cfm-search-shell">
              <PatientSearch
                onSelect={(doc) => void state.bindPatientContext(toPatientProfile(doc, blankPatientForm))}
              />
            </div>
            <button
              type="button"
              className="cfm-secondary-btn"
              onClick={() => navigate("/reception")}
              title="Open reception to register new patient"
            >
              Add Patient
            </button>
            {state.patient && (
              <button
                type="button"
                className="cfm-secondary-btn"
                onClick={state.resetContext}
              >
                {t("mobile_end_session")}
              </button>
            )}
          </div>

          {state.patient && (
            <div className="cfm-patient-card">
              <div className="cfm-patient-main">
                <h2>
                  {state.patient.name || t("mobile_unknown_patient")}
                  <span>#{state.patient.patient_id}</span>
                </h2>
                {paymentBadge && (
                  <div className={`cfm-payment-pill ${paymentBadge.tone}`}>
                    <span>{paymentBadge.label}</span>
                    {paymentBadge.tone !== "neutral" && (
                      <strong>₹{Math.abs(Number(paymentBadge.amount || 0)).toLocaleString("en-IN")}</strong>
                    )}
                  </div>
                )}
                <p>
                  {state.patient.mobile || state.patient.phone || t("mobile_no_phone")}
                  {state.patient.gender ? ` | ${state.patient.gender}` : ""}
                  {state.patient.age ? ` | ${state.patient.age}` : ""}
                </p>
                <button
                  type="button"
                  className="cfm-edit-patient-btn"
                  onClick={() => setShowEditModal(true)}
                  style={{
                    background: 'rgba(3, 105, 161, 0.1)',
                    color: '#0369a1',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    marginTop: '8px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  Edit Profile
                </button>
              </div>
              <div className="cfm-patient-meta">
                <span>
                  {t("mobile_visit_label")}: {visitIdValue || "-"}
                </span>
                <span className={state.loadingVisit ? "is-loading" : ""}>
                  {state.loadingVisit ? t("mobile_syncing") : t("mobile_ready")}
                </span>
              </div>
            </div>
          )}

          {isTouchViewport && (
            <div className="cfm-quick-actions">
              {tabs.map((tab) => {
                const locked = Boolean(tab.requiresPatient && !hasPatientContext);
                return (
                  <button
                    key={`quick-${tab.key}`}
                    type="button"
                    className={`cfm-action-chip ${activeWindow === tab.key ? "is-active" : ""}`}
                    onClick={() => !locked && setActiveWindow(tab.key)}
                    disabled={locked}
                  >
                    <span>{tab.icon}</span>
                    <em>{tab.shortLabel}</em>
                  </button>
                );
              })}
            </div>
          )}

          {!isTouchViewport && (
            <nav className="cfm-top-nav" aria-label={t("mobile_nav_aria")}>
              {tabs
                .filter((tab) => !(tab.requiresPatient && !hasPatientContext))
                .map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`cfm-nav-btn ${activeWindow === tab.key ? "is-active" : ""}`}
                    onClick={() => setActiveWindow(tab.key)}
                  >
                    <span>{tab.icon}</span>
                    <em>{tab.label}</em>
                  </button>
                ))}
              {!hasPatientContext && tabs.some((t) => t.requiresPatient) && (
                <div className="cfm-nav-hint">
                  Select a patient to unlock clinical tabs
                </div>
              )}
            </nav>
          )}
        </header>

        <div className="cfm-content">{renderWindow()}</div>
      </section>

      {isTouchViewport && (
        <nav className="cfm-bottom-nav" aria-label={t("mobile_nav_aria")}>
          {tabs.map((tab) => {
            const locked = Boolean(tab.requiresPatient && !hasPatientContext);
            return (
              <button
                key={tab.key}
                type="button"
                className={`cfm-nav-btn ${activeWindow === tab.key ? "is-active" : ""}`}
                onClick={() => !locked && setActiveWindow(tab.key)}
                disabled={locked}
              >
                <span>{tab.icon}</span>
                <em>{tab.shortLabel}</em>
              </button>
            );
          })}
        </nav>
      )}

      {state.patient && (
        <PatientEditModal
          patient={state.patient}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={async (data) => { await state.updatePatient(String(state.patient?.patient_id), data); }}
        />
      )}
    </main>
  );
}
